import { useEffect, useState } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { NotificationToast } from "../../../../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../../../../context/StateContext";
import { FileImportHistoryService } from "../../../../../../../../../../services/file.import.history.service";
import ProjectService from "../../../../../../../../../../services/project.service";
import { FileImportKeys, ProjectKeys } from "../../../../../../../../../../util/queries";

interface ActionProjectFilesProps {
  projectId: number;
}
export const useActionProjectFiles = ({ projectId }: ActionProjectFilesProps) => {
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  const [hasErrors, setHasErrors] = useState<boolean>();

  const [filesList, setFilesList] = useState<any[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(15);
  const CACHE_TIME = 60 * 60 * 1000; // 1 hour

  const {
    data,
    refetch: refetchProjectFiles,
    isLoading: isLoadingProjectFiles,
  } = useQuery({
    queryKey: FileImportKeys.findByTableName([projectId]),
    queryFn: () =>
      FileImportHistoryService.findByTableName({
        reference_key: projectId,
        table: "PROJECTS",
        company_id: currentUser?.companyId,
      }),
    enabled: !!currentUser?.id && !!currentUser.companyId,
    refetchOnWindowFocus: false,
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: CACHE_TIME,
  });

  const { mutate: uploadCSV } = useMutation({
    mutationKey: ProjectKeys.uploadCsv(),
    mutationFn: ({ file }: { file: FormData }) =>
      ProjectService.uploadCSV(currentUser?.id ?? 0, projectId, file),
    onSuccess: () => {
      NotificationToast.success(`${translate("operationPerformedSuccessfully")}!`);
      refetchProjectFiles();
    },
    onError: () => {
      NotificationToast.error(
        translate("thereWasAProblemTryingToImportTheFilePleaseRefreshThePage")
      );
    },
  });

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  useEffect(() => {
    if (data && data?.data) {
      setFilesList(data?.data);
      setTotal(data?.data.length);
    }
  }, [data]);

  return {
    variables: {
      hasErrors,
      page,
      pageSize,
      isLoadingProjectFiles,
      filesList,
      total,
    },
    uploadCSV,
    refetchProjectFiles,
    setPageSize,
    setHasErrors,
    setCurrentPage,
  };
};
